import { signal } from '@preact/signals';
import { en } from './en';

export type Lang = 'en' | 'ru' | 'zh' | 'ja';
export const LANGS: { id: Lang; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'ru', label: 'Русский' },
  { id: 'zh', label: '简体中文' },
  { id: 'ja', label: '日本語' },
];

type Dict = Record<string, string>;
const dicts: Partial<Record<Lang, Dict>> = { en };
const loaders: Record<Lang, () => Promise<Dict>> = {
  en: async () => en,
  ru: () => import('./ru').then((m) => m.ru),
  zh: () => import('./zh').then((m) => m.zh),
  ja: () => import('./ja').then((m) => m.ja),
};

export const lang = signal<Lang>('en');

export function detectLang(): Lang {
  // `?lang=ru` in the URL wins and is remembered
  const fromUrl = new URLSearchParams(location.search).get('lang') as Lang | null;
  if (fromUrl && loaders[fromUrl]) {
    localStorage.setItem('knight.lang', fromUrl);
    return fromUrl;
  }
  const saved = localStorage.getItem('knight.lang') as Lang | null;
  if (saved && loaders[saved]) return saved;
  // otherwise follow the device: scan all preferred languages, first supported wins, else English
  const prefs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en'];
  for (const raw of prefs) {
    const n = raw.toLowerCase();
    if (n.startsWith('ru') || n.startsWith('uk') || n.startsWith('be')) return 'ru';
    if (n.startsWith('zh')) return 'zh';
    if (n.startsWith('ja')) return 'ja';
    if (n.startsWith('en')) return 'en';
  }
  return 'en';
}

export async function setLang(l: Lang): Promise<void> {
  if (!dicts[l]) dicts[l] = await loaders[l]();
  localStorage.setItem('knight.lang', l);
  lang.value = l;
  document.documentElement.lang = l;
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const d = dicts[lang.value] ?? en;
  let s = d[key] ?? en[key];
  if (s === undefined) return key;
  if (vars) for (const k of Object.keys(vars)) s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
  return s;
}

/** Name of a content entity: hero, skill, aspect, keystone, item, mob, status, consumable. */
export function tn(kind: string, id: string): string {
  return t(`${kind}.${id}.name`);
}

export function td(kind: string, id: string): string {
  return t(`${kind}.${id}.desc`);
}

export function has(key: string): boolean {
  const d = dicts[lang.value] ?? en;
  return key in d || key in en;
}
